import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import LaunchModal from '../components/LaunchModal';

const PluginsPage: React.FC = () => {
  const [showAllPlugins, setShowAllPlugins] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="bg-gray-950 text-white antialiased">
      {/* Hero Section */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">
            Free Plugin System
            <span className="brand-text-gradient"> for Every Farm Type</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
            Start with the core BarnBills experience, then add exactly the features your 
            operation needs. All plugins are completely free and integrate seamlessly.
          </p>
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              <span>Always free</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              <span>One-click install</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              <span>Custom plugins available</span>
            </div>
          </div>
        </div>
      </section>

      {/* How Plugins Work */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">How the Plugin System Works</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              BarnBills starts with a powerful core system that handles all the essentials. 
              Then you can add specialized features as your operation grows.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h4a1 1 0 011 1v5m-6 0V9a1 1 0 011-1h4a1 1 0 011 1v11"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Start Simple</h3>
              <p className="text-gray-400">
                Begin with core horse management, billing, and records. Perfect for any 
                farm getting started with modern software.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Add What You Need</h3>
              <p className="text-gray-400">
                Install plugins for breeding, medical tracking, show records, or feed management. 
                Only add features your operation actually uses.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Seamless Integration</h3>
              <p className="text-gray-400">
                Every plugin integrates perfectly with your existing data. No complex 
                setup or duplicate entry required.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core System */}
      <section className="py-24 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Core System - Always Included</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              Every BarnBills subscription includes these essential features that every horse operation needs.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">Smart Billing</h3>
              <p className="text-sm text-gray-400">Automated invoicing with real payment processing and professional templates</p>
            </div>
            
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h4a1 1 0 011 1v5m-6 0V9a1 1 0 011-1h4a1 1 0 011 1v11"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">Horse Records</h3>
              <p className="text-sm text-gray-400">Complete digital profiles with basic info, ownership, and stall assignments</p>
            </div>
            
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">Mobile First</h3>
              <p className="text-sm text-gray-400">Works perfectly on any device with offline capabilities and real-time sync</p>
            </div>
            
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">Multi-User Access</h3>
              <p className="text-sm text-gray-400">Role-based permissions for owners, staff, and clients with approval workflows</p>
            </div>
            
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">Multilingual</h3>
              <p className="text-sm text-gray-400">Full English and Spanish interfaces for diverse teams and clients</p>
            </div>
            
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">Horse Transfers</h3>
              <p className="text-sm text-gray-400">Industry-first complete data transfer between farms with full history intact</p>
            </div>
          </div>
        </div>
      </section>

      {/* See Plugins Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">See Plugins</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              Specialized features to enhance your operation. All plugins are completely free 
              and integrate seamlessly with your existing data.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Breeding Management */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
              <div className="absolute top-4 right-4">
                <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                  Coming Soon
                </span>
              </div>
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.871 4A17.926 17.926 0 003 12c0 2.874.673 5.59 1.871 8m14.13 0a17.926 17.926 0 001.87-8c0-2.874-.673-5.59-1.87-8M9 9h1.246a1 1 0 01.961.725l1.586 5.55a1 1 0 00.961.725H15"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Breeding Management</h3>
              <p className="text-gray-400 mb-6">
                Complete breeding records, stallion management, mare cycles, and foaling schedules. 
                Track bloodlines and registration paperwork.
              </p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
              </div>
            </div>

            {/* Medical Records */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
              <div className="absolute top-4 right-4">
                <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                  Coming Soon
                </span>
              </div>
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Medical Sheets</h3>
              <p className="text-gray-400 mb-6">
                Digital medical charts, vaccination schedules, medication tracking, and vet visit logs. 
                Generate professional health reports.
              </p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
              </div>
            </div>

            {/* Feed Management */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
              <div className="absolute top-4 right-4">
                <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                  Coming Soon
                </span>
              </div>
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Feed Management</h3>
              <p className="text-gray-400 mb-6">
                Track feed schedules, nutritional requirements, supplement programs, and inventory management. 
                Monitor costs and consumption patterns.
              </p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
              </div>
            </div>

            {/* Show Records */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
              <div className="absolute top-4 right-4">
                <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                  Coming Soon
                </span>
              </div>
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-5 5v-5zM4.828 7l2.586 2.586a2 2 0 002.828 0L13 7M9 1v6m0 0a3 3 0 003 3h3"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Smart Notification System</h3>
              <p className="text-gray-400 mb-6">
                Automated reminders for vaccinations, feeding schedules, incoming horses, stall changes, 
                and field rotations. Never miss important farm tasks again.
              </p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
              </div>
            </div>

            {/* Training Logs */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
              <div className="absolute top-4 right-4">
                <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                  Coming Soon
                </span>
              </div>
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Training Logs</h3>
              <p className="text-gray-400 mb-6">
                Detailed workout tracking, training schedules, progress notes, and performance analytics. 
                Set goals and monitor improvement over time.
              </p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
              </div>
            </div>

            {/* Pasture Management */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
              <div className="absolute top-4 right-4">
                <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                  Coming Soon
                </span>
              </div>
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Pasture Management</h3>
              <p className="text-gray-400 mb-6">
                Rotation schedules, grass condition monitoring, soil testing records, and seasonal planning. 
                Optimize pasture health and utilization.
              </p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
              </div>
            </div>

            {/* Additional Plugins - Show when expanded */}
            {showAllPlugins && (
              <>
                {/* Equipment & Maintenance */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                      Coming Soon
                    </span>
                  </div>
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Equipment & Maintenance</h3>
                  <p className="text-gray-400 mb-6">
                    Track farm equipment, maintenance schedules, repair logs, and warranty information. 
                    Never miss important equipment servicing again.
                  </p>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                    <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
                  </div>
                </div>

                {/* Inventory Management */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                      Coming Soon
                    </span>
                  </div>
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Inventory Management</h3>
                  <p className="text-gray-400 mb-6">
                    Track supplies, medications, feed levels, and equipment. Set low-stock alerts 
                    and manage vendor relationships for automated ordering.
                  </p>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                    <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
                  </div>
                </div>

                {/* Staff Scheduling */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                      Coming Soon
                    </span>
                  </div>
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a1 1 0 012 0v4m-2 0H4a1 1 0 00-1 1v10a1 1 0 001 1h16a1 1 0 001-1V8a1 1 0 00-1-1h-4M8 7V3a1 1 0 012 0v4M8 7h8"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Staff Scheduling</h3>
                  <p className="text-gray-400 mb-6">
                    Create work schedules, track time off requests, manage shift coverage, 
                    and integrate with payroll systems for seamless operations.
                  </p>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                    <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
                  </div>
                </div>

                {/* Financial Analytics */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                      Coming Soon
                    </span>
                  </div>
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Financial Analytics</h3>
                  <p className="text-gray-400 mb-6">
                    Advanced reporting with profit/loss analysis, expense tracking, 
                    cash flow forecasting, and custom financial dashboards.
                  </p>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                    <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
                  </div>
                </div>

                {/* Insurance Management */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                      Coming Soon
                    </span>
                  </div>
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Insurance Management</h3>
                  <p className="text-gray-400 mb-6">
                    Track horse insurance policies, mortality coverage, liability insurance, 
                    and facility coverage. Automated renewal reminders and claims tracking.
                  </p>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                    <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
                  </div>
                </div>

                {/* Visitor Management */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                      Coming Soon
                    </span>
                  </div>
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Visitor Management</h3>
                  <p className="text-gray-400 mb-6">
                    Digital check-in system for visitors, liability waiver management, 
                    tour scheduling, and safety protocol compliance tracking.
                  </p>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                    <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
                  </div>
                </div>

                {/* Emergency Management */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                      Coming Soon
                    </span>
                  </div>
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Emergency Management</h3>
                  <p className="text-gray-400 mb-6">
                    Emergency contact systems, evacuation plans, incident reporting, 
                    and automated alert notifications for critical situations.
                  </p>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                    <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
                  </div>
                </div>

                {/* Document Management */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                      Coming Soon
                    </span>
                  </div>
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Document Management</h3>
                  <p className="text-gray-400 mb-6">
                    Centralized storage for contracts, registrations, certificates, and photos. 
                    OCR scanning and intelligent document categorization.
                  </p>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                    <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
                  </div>
                </div>

                {/* Weather Integration */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                      Coming Soon
                    </span>
                  </div>
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Weather Integration</h3>
                  <p className="text-gray-400 mb-6">
                    Real-time weather data integration for turnout decisions, training schedules, 
                    and event planning with severe weather alerts.
                  </p>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                    <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
                  </div>
                </div>

                {/* Transport Management */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                      Coming Soon
                    </span>
                  </div>
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Transport Management</h3>
                  <p className="text-gray-400 mb-6">
                    Schedule horse transportation, track health certificates, manage shipping 
                    documentation, and coordinate with transport companies.
                  </p>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                    <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
                  </div>
                </div>

                {/* Client Portal */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                      Coming Soon
                    </span>
                  </div>
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h4a1 1 0 011 1v5m-6 0V9a1 1 0 011-1h4a1 1 0 011 1v11"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Farrier Management</h3>
                  <p className="text-gray-400 mb-6">
                    Schedule farrier appointments, track shoeing history, manage trim cycles, 
                    and maintain detailed hoof care records for optimal horse health.
                  </p>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                    <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
                  </div>
                </div>

                {/* Event Planning */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                      Coming Soon
                    </span>
                  </div>
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a1 1 0 012 0v4m-2 0H4a1 1 0 00-1 1v10a1 1 0 001 1h16a1 1 0 001-1V8a1 1 0 00-1-1h-4M8 7V3a1 1 0 012 0v4M8 7h8"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Event Planning</h3>
                  <p className="text-gray-400 mb-6">
                    Plan farm events, clinics, and shows with registration management, 
                    scheduling tools, and integrated payment processing.
                  </p>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                    <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
                  </div>
                </div>

                {/* Stud Farm Management */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                      Coming Soon
                    </span>
                  </div>
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Stud Farm Management</h3>
                  <p className="text-gray-400 mb-6">
                    Specialized stallion management for stud farms. Connects seamlessly with Breeding 
                    Management plugin for faster paperwork sharing and enhanced communication.
                  </p>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                    <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
                  </div>
                </div>

                {/* Compliance Tracking */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                      Coming Soon
                    </span>
                  </div>
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Compliance Tracking</h3>
                  <p className="text-gray-400 mb-6">
                    Track regulatory compliance, license renewals, inspection schedules, 
                    and maintain audit trails for legal requirements.
                  </p>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                    <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
                  </div>
                </div>

                {/* Marketing & CRM */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-medium">
                      Coming Soon
                    </span>
                  </div>
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Marketing & CRM</h3>
                  <p className="text-gray-400 mb-6">
                    Customer relationship management, email marketing campaigns, 
                    lead tracking, and social media integration for farm promotion.
                  </p>
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                    <p className="text-orange-400 font-medium">Coming in a Future Version of BarnBills</p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* View All Plugins Button */}
          <div className="text-center mt-12">
            <button
              onClick={() => setShowAllPlugins(!showAllPlugins)}
              className="bg-gray-800 hover:bg-gray-700 border border-gray-600 text-gray-300 hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 inline-flex items-center space-x-2"
            >
              <span>{showAllPlugins ? 'Show Fewer Plugins' : 'View ALL Planned Plugins'}</span>
              <svg 
                className={`w-5 h-5 transition-transform duration-200 ${showAllPlugins ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Custom Plugin Development */}
      <section className="py-24 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-500/30 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Need Something Custom?</h2>
            <p className="text-xl text-gray-400 mb-8">
              Don't see a plugin that fits your specific needs? Our development team can create 
              a custom plugin tailored exactly to your operation's requirements.
            </p>
            <div className="space-y-4 mb-8">
              <div className="flex items-center justify-center space-x-6 text-gray-300">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Built by horse industry experts</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Seamless integration guaranteed</span>
                </div>
              </div>
            </div>
            <Link 
              to="/support" 
              className="brand-gradient text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25 inline-flex items-center space-x-2"
            >
              <span>Contact Our Support Team</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Start with BarnBills?
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Begin with the core system and add plugins as your operation grows. 
            Everything you need, nothing you don't.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setIsModalOpen(true)}
              className="brand-gradient text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25"
            >
              Get Notified at Launch
            </button>
            <Link 
              to="/pricing" 
              className="border border-gray-700 text-gray-300 hover:text-white hover:border-gray-600 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      <LaunchModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default PluginsPage; 